import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Target, Users, TrendingUp, Clock, ArrowRight, Plus, Flame, DollarSign, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { projectsAPI } from '../lib/api';
import GettingStarted from '../components/guidance/GettingStarted';
import { formatCurrency } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';

const GENRES = ['all', 'music', 'film', 'art', 'tech', 'fashion', 'gaming', 'education', 'community'];
const SORTS = ['trending', 'newest', 'most_funded', 'ending_soon', 'discovery'];

export default function ProjectsPage() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [genre, setGenre] = useState('all');
  const [sort, setSort] = useState('trending');

  useEffect(() => {
    setLoading(true);
    projectsAPI.list({ genre: genre === 'all' ? '' : genre, sort, limit: 30 })
      .then(res => setProjects(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [genre, sort]);

  return (
    <div className="min-h-screen bg-zinc-950 pb-20" data-testid="projects-page">
      <GettingStarted pageContext="projects" />
      {/* Header */}
      <div className="bg-gradient-to-b from-zinc-900 to-zinc-950 border-b border-zinc-800">
        <div className="max-w-6xl mx-auto px-4 pt-8 pb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#b8ff00]/10 border border-[#b8ff00]/20 rounded-full">
                  <Flame size={12} className="text-[#b8ff00]" />
                  <span className="text-[#b8ff00] text-xs font-bold">Creator Funding</span>
                </span>
              </div>
              <h1 className="font-display font-bold text-3xl md:text-4xl text-white">Discover & Back</h1>
              <p className="text-zinc-500 text-sm mt-1">Back rising talent from $1. Ranked by Discovery Score.</p>
            </div>
            {user && (user.role === 'creator' || user.role === 'vendor') && (
              <Link to="/projects/create">
                <Button className="bg-[#b8ff00] text-black font-bold rounded-full" data-testid="create-project-btn">
                  <Plus size={16} className="mr-1" /> Launch Project
                </Button>
              </Link>
            )}
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-2 mb-4">
            {GENRES.map(g => (
              <button key={g} onClick={() => setGenre(g)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${genre === g ? 'bg-[#b8ff00] text-black' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'}`}
                data-testid={`genre-filter-${g}`}
              >{g === 'all' ? 'All' : g.charAt(0).toUpperCase() + g.slice(1)}</button>
            ))}
          </div>
          <div className="flex gap-2">
            {SORTS.map(s => (
              <button key={s} onClick={() => setSort(s)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${sort === s ? 'bg-zinc-700 text-white' : 'bg-zinc-900 text-zinc-500 hover:bg-zinc-800'}`}
                data-testid={`sort-${s}`}
              >{s.replace(/_/g, ' ')}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Projects Grid */}
      <div className="max-w-6xl mx-auto px-4 mt-6">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => <div key={i} className="h-64 bg-zinc-900 rounded-2xl animate-pulse" />)}
          </div>
        ) : projects.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project, idx) => {
              const fundedPct = project.funded_pct || 0;
              const score = project.discovery_score || 0;
              const deadline = new Date(project.deadline);
              const daysLeft = Math.max(0, Math.ceil((deadline - new Date()) / (1000 * 60 * 60 * 24)));
              return (
                <motion.div key={project.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }}>
                  <Link to={`/projects/${project.id}`} className="block rounded-2xl overflow-hidden bg-zinc-900 border border-zinc-800 hover:border-[#b8ff00]/40 group transition-all" data-testid={`project-card-${project.id}`}>
                    <div className="p-5">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-bold text-zinc-500 uppercase tracking-wider">{project.genre}</span>
                        <div className="flex items-center gap-2">
                          {score > 0 && (
                            <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#b8ff00]/10 border border-[#b8ff00]/20">
                              <Target size={10} className="text-[#b8ff00]" />
                              <span className="text-[10px] font-bold text-[#b8ff00]">{score}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-zinc-800">
                            <Clock size={10} className="text-zinc-500" />
                            <span className="text-[10px] text-zinc-500">{daysLeft}d</span>
                          </div>
                        </div>
                      </div>
                      <h3 className="font-display font-bold text-lg text-white mb-1 group-hover:text-[#b8ff00] transition-colors">{project.title}</h3>
                      <p className="text-xs text-zinc-500 mb-3">by {project.creator_name}</p>
                      <p className="text-sm text-zinc-400 line-clamp-2 mb-4">{project.description}</p>

                      {/* Funding bar */}
                      <div className="mb-3">
                        <div className="flex justify-between text-xs mb-1.5">
                          <span className="text-white font-semibold">{formatCurrency(project.current_funding || 0)}</span>
                          <span className="text-zinc-500">of {formatCurrency(project.funding_goal)}</span>
                        </div>
                        <div className="w-full bg-zinc-800 rounded-full h-2">
                          <div className="h-2 rounded-full bg-gradient-to-r from-[#b8ff00] to-[#00ff88] transition-all" style={{ width: `${Math.min(100, fundedPct)}%` }} />
                        </div>
                      </div>

                      <div className="flex items-center justify-between text-xs text-zinc-500">
                        <span className="flex items-center gap-1"><Users size={11} /> {project.backer_count || 0} backers</span>
                        <span className="flex items-center gap-1"><TrendingUp size={11} /> {fundedPct}% funded</span>
                      </div>

                      <div className="mt-4 pt-3 border-t border-zinc-800 flex items-center justify-between">
                        <span className="text-xs text-zinc-500">Invest from <span className="text-[#b8ff00] font-bold">$1</span></span>
                        <span className="text-xs font-semibold text-[#b8ff00] flex items-center gap-1 group-hover:translate-x-1 transition-transform">Back this <ChevronRight size={12} /></span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-20 bg-zinc-900 rounded-2xl border border-zinc-800">
            <DollarSign size={48} className="mx-auto text-zinc-700 mb-4" />
            <p className="text-zinc-500 mb-4">No projects yet. Be the first to launch!</p>
            {user && (
              <Link to="/projects/create">
                <Button className="bg-[#b8ff00] text-black font-bold rounded-full">Launch a Project</Button>
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
